import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import type {Button, AriaMenuItem} from '@pexip/components';
import {AriaMenu, Color, Icon, IconTypes} from '@pexip/components';

import {TestId} from '../../../test/testIds';
import {CallHistoryItemMoreMenuHeader} from '../CallHistoryItemMoreMenuHeader/CallHistoryItemMoreMenuHeader.view';

export const CallHistoryItemMoreMenu: React.FC<{
    isTouchDevice: boolean;
    isRemoveFavoriteDisabled?: boolean;
    removeFavoriteDisabledReason?: React.ReactNode;
    title?: string;
    meetingId: string;
    handleCallMeetingId?: () => void;
    handleDeleteRecent?: () => void;
    handleRemoveFavorite?: () => void;
    handleAddToFavorites?: () => void;
    button: React.ReactElement<typeof Button>;
    onOpenChange: (state: boolean) => void;
}> = ({
    isTouchDevice,
    isRemoveFavoriteDisabled = false,
    removeFavoriteDisabledReason,
    title,
    meetingId,
    handleCallMeetingId,
    handleDeleteRecent,
    handleRemoveFavorite,
    handleAddToFavorites,
    button,
    onOpenChange,
}) => {
    const {t} = useTranslation();

    const menuContent: AriaMenuItem[] = useMemo(() => {
        return [
            Boolean(handleCallMeetingId) && {
                label: t(
                    'media.call-history.call-this-video-address',
                    'Call this video address',
                ),
                onItemClick: () => handleCallMeetingId?.(),
                testId: TestId.ActionCallMeetingId,
                key: 'call-video-address',
                icon: (
                    <Icon
                        size={isTouchDevice ? 'small' : 'compact'}
                        source={IconTypes.IconPhone}
                    />
                ),
            },
            Boolean(handleAddToFavorites) && {
                label: t(
                    'media.call-history.add-to-favorites',
                    'Add to Favorites',
                ),
                onItemClick: () => handleAddToFavorites?.(),
                testId: TestId.ActionAddToFavorites,
                key: 'add-to-favorites',
                icon: (
                    <Icon
                        size={isTouchDevice ? 'small' : 'compact'}
                        source={IconTypes.IconStar}
                    />
                ),
            },
            Boolean(handleDeleteRecent) && {
                label: t('common.delete', 'Delete'),
                onItemClick: () => handleDeleteRecent?.(),
                testId: TestId.ActionDeleteRecent,
                key: 'delete-recent',
                icon: (
                    <Icon
                        size={isTouchDevice ? 'small' : 'compact'}
                        source={IconTypes.IconTrash}
                    />
                ),
                style: {color: Color.Red70},
            },
            Boolean(handleRemoveFavorite) && {
                label: t(
                    'media.call-history.remove-favorite',
                    'Remove favorite',
                ),
                onItemClick: () => handleRemoveFavorite?.(),
                testId: TestId.ActionRemoveFavorite,
                key: 'remove-favorite',
                isDisabled: isRemoveFavoriteDisabled,
                description:
                    isRemoveFavoriteDisabled && removeFavoriteDisabledReason
                        ? removeFavoriteDisabledReason
                        : undefined,
                icon: (
                    <Icon
                        size={isTouchDevice ? 'small' : 'compact'}
                        source={IconTypes.IconTrash}
                    />
                ),
                style: isRemoveFavoriteDisabled
                    ? undefined
                    : {color: Color.Red70},
            },
        ].filter(Boolean) as AriaMenuItem[];
    }, [
        handleAddToFavorites,
        handleCallMeetingId,
        handleDeleteRecent,
        handleRemoveFavorite,
        isTouchDevice,
        isRemoveFavoriteDisabled,
        removeFavoriteDisabledReason,
        t,
    ]);

    return (
        <AriaMenu
            headerContent={
                <CallHistoryItemMoreMenuHeader
                    isTouchDevice={false}
                    className="pt-4 pl-4 pr-4 pb-1"
                    title={title}
                    meetingId={meetingId}
                />
            }
            onOpenChange={onOpenChange}
            button={button}
            items={menuContent}
            testId="kebab-menu"
        />
    );
};
